import React, { useEffect, useMemo, useState } from 'react';
import { Building2, Users, Briefcase, FileText, Search, Download, AlertTriangle, Clock, MoonStar, Ban, CheckCircle, Plus, Tag, X, RefreshCw } from 'lucide-react';
import { getAllFirmStats, saBulkFirmAction } from '../../lib/db';
import { formatDate } from '../../lib/formatters';
import { PLANS } from '../../lib/plans';
import { useRoute } from '../../lib/router';
import toast from '../../lib/toast';

const PAGE_SIZE = 25;

const KPI = ({ icon: Icon, label, value, color }) => (
  <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
    <div className={`inline-flex p-2 rounded-xl mb-3 ${color}`}>
      <Icon size={18} className="text-white" />
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
    <p className="text-sm text-slate-400 mt-0.5">{label}</p>
  </div>
);

const Alert = ({ icon: Icon, tone, title, count, onClick }) => {
  const tones = {
    amber: 'bg-amber-900/20 border-amber-800/50 text-amber-300',
    red:   'bg-red-900/20 border-red-800/50 text-red-300',
    slate: 'bg-slate-800/50 border-slate-700 text-slate-300',
  };
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-sm text-left transition w-full ${tones[tone]} ${onClick ? 'hover:brightness-125 cursor-pointer' : 'cursor-default'}`}
    >
      <Icon size={16} />
      <div className="flex-1">
        <p className="font-medium">{title}</p>
      </div>
      <span className="text-lg font-bold tabular-nums">{count}</span>
    </button>
  );
};

function timeAgo(ts) {
  if (!ts) return 'Never';
  const diff = Date.now() - new Date(ts).getTime();
  const days = Math.floor(diff / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  return formatDate(ts);
}

function trialDaysLeft(trialEndsAt) {
  if (!trialEndsAt) return null;
  const ms = new Date(trialEndsAt).getTime() - Date.now();
  return Math.ceil(ms / 86400000);
}

function planLabel(key) {
  return PLANS[key]?.name || (key ? key.charAt(0).toUpperCase() + key.slice(1) : '—');
}

function planTone(key) {
  switch (key) {
    case 'pro':         return 'bg-indigo-900/40 text-indigo-300';
    case 'basic':       return 'bg-blue-900/40 text-blue-300';
    case 'ai':
    case 'enterprise':  return 'bg-purple-900/40 text-purple-300';
    case 'trial':       return 'bg-slate-700 text-slate-300';
    default:            return 'bg-slate-700 text-slate-400';
  }
}

function statusTone(status, suspendedAt) {
  if (suspendedAt) return 'bg-red-900/40 text-red-300';
  switch (status) {
    case 'active':     return 'bg-emerald-900/40 text-emerald-300';
    case 'trial':      return 'bg-amber-900/40 text-amber-300';
    case 'suspended':  return 'bg-red-900/40 text-red-300';
    case 'cancelled':  return 'bg-slate-700 text-slate-400';
    default:           return 'bg-slate-700 text-slate-400';
  }
}

function statusLabel(status, suspendedAt) {
  if (suspendedAt || status === 'suspended') return 'Suspended';
  if (status === 'active') return 'Active';
  if (status === 'trial')  return 'Trial';
  if (status === 'cancelled') return 'Cancelled';
  return status || 'Unknown';
}

function toCsv(firms) {
  const headers = [
    'firm_id','firm_name','firm_email','status','plan','trial_ends_at',
    'created_at','last_activity','user_count','matter_count','invoice_count',
    'seat_limit','seats_in_use',
    'paystack_customer_code','paystack_subscription_code',
  ];
  const rows = firms.map((f) => [
    f.firm_id, f.firm_name, f.firm_email,
    statusLabel(f.subscription_status, f.suspended_at),
    f.subscription_plan || '',
    f.trial_ends_at || '',
    f.created_at || '',
    f.last_activity || '',
    f.user_count || 0,
    f.matter_count || 0,
    f.invoice_count || 0,
    // seat_limit may be null (= unlimited). Write empty string for
    // null so the CSV stays readable in Excel.
    f.seat_limit ?? '',
    f.seats_in_use ?? '',
    f.paystack_customer_code || '',
    f.paystack_subscription_code || '',
  ]);
  const escape = (s) => {
    const str = String(s ?? '');
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };
  return [headers, ...rows].map((r) => r.map(escape).join(',')).join('\n');
}

function downloadCsv(filename, contents) {
  const blob = new Blob([contents], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const STATUS_FILTERS = ['all', 'trial', 'active', 'suspended'];
const SORT_OPTIONS = [
  { key: 'created_desc',    label: 'Newest first' },
  { key: 'created_asc',     label: 'Oldest first' },
  { key: 'activity_desc',   label: 'Most recent activity' },
  { key: 'users_desc',      label: 'Most users' },
  // "Closest to seat cap" surfaces upsell candidates and over-cap
  // anomalies. Firms with no cap configured sink to the bottom.
  { key: 'seat_pct_desc',   label: 'Closest to seat cap' },
  { key: 'matters_desc',    label: 'Most case files' },
  { key: 'name_asc',        label: 'Firm name (A–Z)' },
];

export default function SuperAdminDashboardPage() {
  const [firms,   setFirms]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [, navigate] = useRoute();

  const [query,  setQuery]  = useState('');
  const [status, setStatus] = useState('all');
  const [plan,   setPlan]   = useState('all');
  const [sort,   setSort]   = useState('created_desc');
  const [page,   setPage]   = useState(0);
  const [alertFilter, setAlertFilter] = useState(null); // 'trial_expiring' | 'dormant' | 'suspended' | null
  const [selected, setSelected] = useState(() => new Set()); // firm_ids
  const [bulkWorking, setBulkWorking] = useState(false);

  // Initial load + auto-refresh on tab focus / visibility change.
  // The dashboard's data changes whenever a super-admin updates a
  // firm's seat / storage cap from the firm-detail page — without
  // refetching on focus, navigating back here would show stale
  // values until a manual refresh. Refetching on visibility change
  // (when the user switches tabs back) catches this without burning
  // requests while the page is unfocused.
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      getAllFirmStats().then(({ data, error: err }) => {
        if (cancelled) return;
        if (err) setError(err.message || 'Could not load firms');
        else setFirms(data || []);
        setLoading(false);
      });
    };
    load();
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    const onFocus = () => load();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  // Reset to first page whenever filters change
  useEffect(() => { setPage(0); }, [query, status, plan, sort, alertFilter]);

  const refreshFirms = () => {
    setLoading(true);
    getAllFirmStats().then(({ data, error: err }) => {
      if (err) setError(err.message || 'Could not load firms');
      else setFirms(data || []);
      setLoading(false);
    });
  };

  const toggleSelect = (firmId) => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(firmId)) next.delete(firmId);
      else next.add(firmId);
      return next;
    });
  };

  const runBulk = async (action, payload, confirmMsg) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBulkWorking(true);
    const { data, error: err } = await saBulkFirmAction(ids, action, payload || {});
    setBulkWorking(false);
    if (err) { toast.error(err.message || 'Bulk action failed'); return; }
    toast.success(`${action.replace('_', ' ')}: ${data?.count || 0} firm${data?.count === 1 ? '' : 's'} updated.`);
    setSelected(new Set());
    refreshFirms();
  };

  const bulkSuspend = () => {
    const reason = window.prompt(`Reason for suspending ${selected.size} firm${selected.size === 1 ? '' : 's'}? (optional)`, '');
    if (reason === null) return;
    runBulk('suspend', { reason });
  };
  const bulkUnsuspend = () =>
    runBulk('unsuspend', {}, `Restore access for ${selected.size} firm${selected.size === 1 ? '' : 's'}?`);
  const bulkExtendTrial = () => {
    const raw = window.prompt('Extend trial by how many days? (1–365)', '14');
    if (!raw) return;
    const days = parseInt(raw, 10);
    if (!Number.isFinite(days) || days < 1 || days > 365) {
      toast.warning('Enter a whole number between 1 and 365.');
      return;
    }
    runBulk('extend_trial', { days });
  };
  const bulkSetPlan = (newPlan) => {
    if (!newPlan) return;
    runBulk('set_plan', { plan: newPlan }, `Change plan to "${newPlan}" for ${selected.size} firm${selected.size === 1 ? '' : 's'}?`);
  };

  const planOptions = useMemo(() => {
    const set = new Set(firms.map((f) => f.subscription_plan).filter(Boolean));
    return ['all', ...Array.from(set)];
  }, [firms]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const dormantCutoff = Date.now() - 30 * 86400000;
    let out = firms.filter((f) => {
      if (q && !(f.firm_name?.toLowerCase().includes(q) || f.firm_email?.toLowerCase().includes(q))) {
        return false;
      }
      if (status !== 'all') {
        const eff = f.suspended_at ? 'suspended' : f.subscription_status;
        if (eff !== status) return false;
      }
      if (plan !== 'all' && f.subscription_plan !== plan) return false;
      if (alertFilter === 'trial_expiring') {
        const days = trialDaysLeft(f.trial_ends_at);
        if (!(f.subscription_status === 'trial' && days !== null && days <= 7)) return false;
      }
      if (alertFilter === 'dormant') {
        const last = f.last_activity ? new Date(f.last_activity).getTime() : 0;
        if (last >= dormantCutoff) return false;
      }
      if (alertFilter === 'suspended') {
        if (!f.suspended_at && f.subscription_status !== 'suspended') return false;
      }
      return true;
    });

    out = [...out].sort((a, b) => {
      switch (sort) {
        case 'created_asc':   return new Date(a.created_at) - new Date(b.created_at);
        case 'activity_desc': return new Date(b.last_activity || 0) - new Date(a.last_activity || 0);
        case 'users_desc':    return Number(b.user_count   || 0) - Number(a.user_count   || 0);
        case 'seat_pct_desc': {
          // % capacity used. Firms with no cap (seat_limit == null) get
          // -1 so they sort to the bottom — we want capped firms near
          // their limit at the top.
          const pct = (f) => {
            if (f.seat_limit == null || f.seat_limit <= 0) return -1;
            return Number(f.seats_in_use || 0) / Number(f.seat_limit);
          };
          return pct(b) - pct(a);
        }
        case 'matters_desc':  return Number(b.matter_count || 0) - Number(a.matter_count || 0);
        case 'name_asc':      return (a.firm_name || '').localeCompare(b.firm_name || '');
        case 'created_desc':
        default:              return new Date(b.created_at) - new Date(a.created_at);
      }
    });

    return out;
  }, [firms, query, status, plan, sort, alertFilter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const visible = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // KPIs / alerts always reflect the full set, not the filtered view.
  const totals = useMemo(() => {
    const trials = firms.filter((f) => f.subscription_status === 'trial' && !f.suspended_at).length;
    const active = firms.filter((f) => f.subscription_status === 'active' && !f.suspended_at).length;
    const suspended = firms.filter((f) => f.suspended_at || f.subscription_status === 'suspended').length;
    const expiringSoon = firms.filter((f) => {
      const d = trialDaysLeft(f.trial_ends_at);
      return f.subscription_status === 'trial' && d !== null && d <= 7 && !f.suspended_at;
    }).length;
    const cutoff = Date.now() - 30 * 86400000;
    const dormant = firms.filter((f) => {
      const last = f.last_activity ? new Date(f.last_activity).getTime() : 0;
      return last < cutoff && !f.suspended_at;
    }).length;
    // Seat economics across the platform. Only count firms with a cap
    // configured — firms on enterprise / no-cap plans get NULL
    // seat_limit and shouldn't pollute the "seats sold" figure.
    const cappedFirms = firms.filter(
      (f) => f.seat_limit != null && !f.suspended_at,
    );
    const seatsSold   = cappedFirms.reduce((s, f) => s + Number(f.seat_limit || 0), 0);
    const seatsInUse  = cappedFirms.reduce((s, f) => s + Number(f.seats_in_use || 0), 0);
    // Firms at or over 100% capacity — upsell candidates AND
    // potential billing miscalibration to investigate.
    const atCap = cappedFirms.filter(
      (f) => Number(f.seats_in_use || 0) >= Number(f.seat_limit || 0),
    ).length;
    return { trials, active, suspended, expiringSoon, dormant, seatsSold, seatsInUse, atCap };
  }, [firms]);

  if (loading) return <div className="text-center py-20 text-slate-500">Loading…</div>;
  if (error)   return <div className="text-center py-20 text-red-400">{error}</div>;

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-white">System Overview</h1>
          <p className="text-slate-400 text-sm mt-0.5">{firms.length} firms registered</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={refreshFirms}
            disabled={loading}
            title="Refetch the firm list (also auto-refreshes on tab focus)"
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-white text-sm font-medium transition"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            onClick={() => downloadCsv(`firms-${new Date().toISOString().slice(0,10)}.csv`, toCsv(filtered))}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium transition"
          >
            <Download size={14} /> Export {filtered.length !== firms.length ? 'filtered' : 'all'} ({filtered.length})
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KPI icon={Building2} label="Total firms"  value={firms.length}    color="bg-indigo-600" />
        <KPI icon={Users}     label="Active"       value={totals.active}   color="bg-emerald-600" />
        <KPI icon={Briefcase} label="On trial"     value={totals.trials}   color="bg-amber-600" />
        <KPI icon={FileText}  label="Suspended"    value={totals.suspended} color="bg-red-600" />
      </div>

      {/* Platform-wide seat aggregates were here. Removed — per-firm
          capacity belongs on the firm detail page, not the platform
          overview. With only a handful of capped firms the aggregate
          looked like one specific firm's data, which was confusing.
          The Seats and Storage columns in the table below still give
          a per-firm view and a "Firms at or over cap" filter exists
          via the sort option "Closest to seat cap". */}
      {totals.atCap > 0 && (
        <div className="rounded-xl border border-red-700/40 bg-red-900/15 px-4 py-3 text-sm">
          <span className="font-semibold text-red-400">{totals.atCap}</span>{' '}
          <span className="text-red-200">
            firm{totals.atCap === 1 ? '' : 's'} at or over their seat cap — sort by
            "Closest to seat cap" to review.
          </span>
        </div>
      )}

      {/* Health alerts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Alert
          icon={Clock}
          tone="amber"
          title="Trials expiring in 7 days"
          count={totals.expiringSoon}
          onClick={totals.expiringSoon ? () => { setAlertFilter('trial_expiring'); setStatus('all'); } : null}
        />
        <Alert
          icon={MoonStar}
          tone="slate"
          title="Dormant 30+ days"
          count={totals.dormant}
          onClick={totals.dormant ? () => { setAlertFilter('dormant'); setStatus('all'); } : null}
        />
        <Alert
          icon={AlertTriangle}
          tone="red"
          title="Suspended firms"
          count={totals.suspended}
          onClick={totals.suspended ? () => { setAlertFilter('suspended'); setStatus('all'); } : null}
        />
      </div>

      {/* Filters */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by firm name or email…"
              className="w-full pl-9 pr-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-sm text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {SORT_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-slate-500 mr-1">Status:</span>
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`px-2.5 py-1 rounded-lg capitalize transition ${
                status === s
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              {s}
            </button>
          ))}
          <span className="text-slate-500 ml-3 mr-1">Plan:</span>
          {planOptions.map((p) => (
            <button
              key={p}
              onClick={() => setPlan(p)}
              className={`px-2.5 py-1 rounded-lg capitalize transition ${
                plan === p
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              {p === 'all' ? 'all' : planLabel(p)}
            </button>
          ))}
          {alertFilter && (
            <button
              onClick={() => setAlertFilter(null)}
              className="ml-auto px-2.5 py-1 rounded-lg bg-amber-900/40 text-amber-300 hover:bg-amber-900/60"
            >
              Clear health filter
            </button>
          )}
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="sticky top-14 z-30 bg-indigo-950/95 border border-indigo-800 backdrop-blur rounded-2xl px-4 py-3 flex items-center gap-3 flex-wrap shadow-lg">
          <span className="text-sm font-medium text-white">
            {selected.size} selected
          </span>
          <span className="text-slate-500">·</span>
          <button
            onClick={bulkSuspend}
            disabled={bulkWorking}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-700 hover:bg-amber-600 text-white text-xs font-medium transition disabled:opacity-50"
          >
            <Ban size={12} /> Suspend
          </button>
          <button
            onClick={bulkUnsuspend}
            disabled={bulkWorking}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-medium transition disabled:opacity-50"
          >
            <CheckCircle size={12} /> Unsuspend
          </button>
          <button
            onClick={bulkExtendTrial}
            disabled={bulkWorking}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-700 hover:bg-indigo-600 text-white text-xs font-medium transition disabled:opacity-50"
          >
            <Plus size={12} /> Extend trial
          </button>
          <div className="flex items-center gap-1.5">
            <Tag size={12} className="text-slate-400" />
            <select
              disabled={bulkWorking}
              onChange={(e) => { const v = e.target.value; e.target.value = ''; bulkSetPlan(v); }}
              defaultValue=""
              className="px-2 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-white text-xs disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="" disabled>Change plan…</option>
              <option value="trial">Trial</option>
              <option value="basic">Basic</option>
              <option value="pro">Pro</option>
              <option value="ai">Enterprise</option>
            </select>
          </div>
          <button
            onClick={() => setSelected(new Set())}
            className="ml-auto flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs"
          >
            <X size={12} /> Clear
          </button>
        </div>
      )}

      {/* Firms table */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-white">
            {filtered.length === firms.length
              ? `All firms (${firms.length})`
              : `${filtered.length} of ${firms.length} firms`}
          </h2>
          <div className="text-xs text-slate-500">
            Page {page + 1} of {pageCount}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-800/40">
              <tr>
                <th className="px-3 py-3 w-8">
                  <input
                    type="checkbox"
                    aria-label="Select all on this page"
                    checked={visible.length > 0 && visible.every((f) => selected.has(f.firm_id))}
                    onChange={(e) => {
                      setSelected((cur) => {
                        const next = new Set(cur);
                        if (e.target.checked) visible.forEach((f) => next.add(f.firm_id));
                        else                  visible.forEach((f) => next.delete(f.firm_id));
                        return next;
                      });
                    }}
                    className="rounded border-slate-600 bg-slate-800 text-indigo-500 focus:ring-indigo-500 focus:ring-offset-slate-900"
                  />
                </th>
                <th className="text-left px-5 py-3 font-medium">Firm</th>
                <th className="text-left px-5 py-3 font-medium">Plan</th>
                <th className="text-left px-5 py-3 font-medium">Status</th>
                <th className="text-right px-5 py-3 font-medium" title="Seats in use vs. seat cap (or total users if no cap configured)">Seats</th>
                <th className="text-right px-5 py-3 font-medium" title="Storage used vs. effective limit (base plan + add-on)">Storage</th>
                <th className="text-right px-5 py-3 font-medium">Cases</th>
                <th className="text-right px-5 py-3 font-medium">Invoices</th>
                <th className="text-left px-5 py-3 font-medium">Last active</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {visible.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center py-12 text-slate-500">
                    No firms match the current filters.
                  </td>
                </tr>
              )}
              {visible.map((f) => {
                const days = trialDaysLeft(f.trial_ends_at);
                const isTrial = f.subscription_status === 'trial' && !f.suspended_at;
                const isSelected = selected.has(f.firm_id);
                return (
                  <tr
                    key={f.firm_id}
                    onClick={() => navigate(`/superadmin/firms/${f.firm_id}`)}
                    className={`cursor-pointer transition ${isSelected ? 'bg-indigo-950/30' : 'hover:bg-slate-800/50'}`}
                  >
                    <td className="px-3 py-3 w-8" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${f.firm_name}`}
                        checked={isSelected}
                        onChange={() => toggleSelect(f.firm_id)}
                        className="rounded border-slate-600 bg-slate-800 text-indigo-500 focus:ring-indigo-500 focus:ring-offset-slate-900"
                      />
                    </td>
                    <td className="px-5 py-3">
                      <p className="font-medium text-white">{f.firm_name}</p>
                      <p className="text-xs text-slate-500">{f.firm_email || '—'}</p>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${planTone(f.subscription_plan)}`}>
                        {planLabel(f.subscription_plan)}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusTone(f.subscription_status, f.suspended_at)}`}>
                        {statusLabel(f.subscription_status, f.suspended_at)}
                      </span>
                      {isTrial && days !== null && (
                        <span className={`ml-2 text-[11px] ${days <= 3 ? 'text-red-400' : days <= 7 ? 'text-amber-400' : 'text-slate-500'}`}>
                          {days <= 0 ? 'expired' : `${days}d left`}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">
                      {(() => {
                        // Seat-usage cell. Three render modes:
                        //   * seat_limit configured  → "in_use / limit"
                        //     color-coded by capacity (red ≥100%, amber ≥80%,
                        //     emerald otherwise).
                        //   * seat_limit NULL        → fall back to the
                        //     legacy user_count so older firms / enterprise
                        //     plans still show a meaningful number.
                        const limit = f.seat_limit;
                        const inUse = f.seats_in_use != null ? f.seats_in_use : f.user_count;
                        if (limit == null) {
                          return (
                            <span className="text-slate-300" title="No seat cap configured">
                              {inUse}
                            </span>
                          );
                        }
                        const pct = limit > 0 ? inUse / limit : 0;
                        const tone =
                          pct >= 1     ? 'text-red-400 font-semibold'
                          : pct >= 0.8 ? 'text-amber-400'
                          :              'text-emerald-400';
                        return (
                          <span
                            className={tone}
                            title={
                              pct >= 1
                                ? `At or over cap: ${inUse} of ${limit} seats in use`
                                : `${inUse} of ${limit} seats in use (${Math.round(pct * 100)}%)`
                            }
                          >
                            {inUse} / {limit}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">
                      {(() => {
                        // Storage cell — same color-coding pattern as Seats.
                        // Effective limit = base + addon. Display in GB so
                        // numbers stay readable; MB if both sides are small.
                        const baseMb  = f.storage_limit_mb;
                        const addonMb = Number(f.storage_addon_mb || 0);
                        const usedMb  = Number(f.storage_used_mb  || 0);
                        if (baseMb == null) {
                          // Unlimited / no cap configured.
                          return (
                            <span className="text-slate-300" title="No storage cap configured">
                              {usedMb >= 1024
                                ? `${(usedMb / 1024).toFixed(1)} GB`
                                : `${usedMb} MB`}
                            </span>
                          );
                        }
                        const effective = Number(baseMb) + addonMb;
                        const pct = effective > 0 ? usedMb / effective : 0;
                        const tone =
                          pct >= 1     ? 'text-red-400 font-semibold'
                          : pct >= 0.8 ? 'text-amber-400'
                          :              'text-emerald-400';
                        const fmt = (mb) =>
                          mb >= 1024
                            ? `${(mb / 1024).toFixed(mb >= 10240 ? 0 : 1)} GB`
                            : `${mb} MB`;
                        return (
                          <span
                            className={tone}
                            title={
                              `${fmt(usedMb)} of ${fmt(effective)} in use` +
                              (addonMb > 0
                                ? ` (base ${fmt(baseMb)} + add-on ${fmt(addonMb)})`
                                : '') +
                              ` — ${Math.round(pct * 100)}%`
                            }
                          >
                            {fmt(usedMb)} / {fmt(effective)}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-5 py-3 text-right text-slate-300 tabular-nums">{f.matter_count}</td>
                    <td className="px-5 py-3 text-right text-slate-300 tabular-nums">{f.invoice_count}</td>
                    <td className="px-5 py-3 text-slate-400 text-xs">{timeAgo(f.last_activity)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {pageCount > 1 && (
          <div className="px-5 py-3 border-t border-slate-800 flex items-center justify-between text-xs">
            <span className="text-slate-500">
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 disabled:opacity-40"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(Math.min(pageCount - 1, page + 1))}
                disabled={page >= pageCount - 1}
                className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
